/*
 * Módulo para controlar la instancia del chat Tawk.
 *
 * Este módulo hace de "puente" para controla la instancia del chat que crea la
 * web en la variable global Tawk_API. Exporta algunas funciones, según
 * necesidad, definidas en: https://www.tawk.to/javascript-api/
 *
 * Este módulo no se está encargando, a día de hoy, de crear la instancia del
 * chat. Se presupone que ya existe. Si no es el caso, las funciones simplemente
 * no ejecutarán nada.
 *
 * El módulo encargado de crear el chat, que está en la web "vieja", usa un
 * objeto global Tawk_API_listeners para poder modificar los handlers de los
 * eventos que lanza el chat. Los listeners se deben registrar al crear el
 * widget, por lo que se ha optado por poner un interceptor.
 */

/** API del chat de tawk. */
declare let Tawk_API: TawkChat | undefined;

/**
 * Objeto intermedio para poder modificar los listeners después de la creación
 * del widget.
 */
declare let Tawk_API_listeners: Partial<TawkChatListeners>;

/* Una apaño guarrete para inicializar el objeto de Tawk_API_listeners por si el
 * módulo se ejecuta antes que el chat. Así no depende del orden. Lo ideal será
 * cuando se cree el chat desde typescript. Esto podrá desaparecer.*/
if (typeof Tawk_API === "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Tawk_API = undefined;
}
if (typeof Tawk_API_listeners === "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Tawk_API_listeners = {};
}

/** Posibles estados del chat. */
export type TawkStatus = "online" | "away" | "offline";

/** Definición de los listeners del chat. */
// Únicamente se han añadido las que voy a usar ahora. Poner el resto según
// hagan falta.
export interface TawkChatListeners {
  /** Se invoca cuando el chat se oculta. */
  onChatHidden: () => void;

  /** Se invoca cuando el chat se muestra maximizado. */
  onChatMaximized: () => void;

  /** Se invoca después de que se haya renderizado el chat. */
  onLoad: () => void;

  /** Se invoca cuando cambia el estado del chat. */
  onStatusChange: (status: TawkStatus) => void;
}

/** Definición del objeto del chat. */
// Únicamente se han añadido las que voy a usar ahora. Poner el resto según
// hagan falta.
export interface TawkChat {
  /**
   * Indica si el widget del chat existe en la página. Lo que no quiere decir
   * que esté inicializado.
   */
  exists: () => boolean;

  /** Recupera el estado del chat o null si no hay widget. */
  getStatus: () => TawkStatus | null;

  /** Devuelve is el chat se encuentra maximizado. */
  isChatMaximized: () => boolean;

  /** Maximiza el widget del chat. */
  maximize: () => void;

  /** Abre el chat como si fuera un popup. */
  popup: () => void;

  /** Establece el listener para el evento "onChatHidden" */
  setOnChatHiddenHandler: (handler: TawkChatListeners["onChatHidden"]) => void;

  /** Establece el listener para el evento "onChatMaximized" */
  setOnChatMaximizedHandler: (handler: TawkChatListeners["onChatMaximized"]) => void;

  /** Establece el listener para el evento "onLoad" */
  setOnLoadHandler: (handler: TawkChatListeners["onLoad"]) => void;

  /** Establece el listener para el evento "onStatusChange" */
  setOnStatusChangeHandler: (handler: TawkChatListeners["onStatusChange"]) => void;
}

/** Implementación de un proxy que delega en la instacia de Tawk si existe. */
const TawkProxy: TawkChat = {
  exists: () => Tawk_API != null,

  getStatus: () => {
    if (Tawk_API != null && Tawk_API.getStatus != null) {
      return Tawk_API.getStatus();
    } else {
      return null;
    }
  },

  isChatMaximized: () => {
    if (Tawk_API != null && Tawk_API.isChatMaximized != null) {
      return Tawk_API.isChatMaximized();
    } else {
      return false;
    }
  },

  maximize: () => {
    if (Tawk_API != null && Tawk_API.maximize != null) {
      Tawk_API.maximize();
    }
  },

  popup: () => {
    if (Tawk_API != null && Tawk_API.popup != null) {
      Tawk_API.popup();
    }
  },

  setOnChatHiddenHandler: (handler) => (Tawk_API_listeners.onChatHidden = handler),

  setOnChatMaximizedHandler: (handler) => (Tawk_API_listeners.onChatMaximized = handler),

  setOnLoadHandler: (handler) => (Tawk_API_listeners.onLoad = handler),

  setOnStatusChangeHandler: (handler) => (Tawk_API_listeners.onStatusChange = handler),
};

export default TawkProxy;
